import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Divider,
  Avatar,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  PeopleAlt,
  Assignment,
  Block,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Warning,
  PersonAdd,
  PersonOff,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  History,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';

import { dashboardService } from '../../services/dashboardService';
import { contractService } from '../../services/contractService';
import { clientService } from '../../services/clientService';
import invoiceService from '../../services/invoiceService';
import ticketService from '../../services/ticketService';
import ClientesTable from '../../components/client/ClientesTable';
import ContratosTable from '../../components/contract/ContratosTable';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { Client, Contract } from '../../types';
import Swal from 'sweetalert2';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtInt = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR');

const fmtMes = (mes: string) => {
  if (!mes) return '';
  const [ano, m] = mes.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[parseInt(m) - 1]}/${(ano ?? '').slice(2)}`;
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiProps {
  titulo: string;
  valor: React.ReactNode;
  subtitulo?: string;
  icone: React.ReactElement;
  cor: string;
  loading?: boolean;
  badge?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}

const KpiCard: React.FC<KpiProps> = ({ titulo, valor, subtitulo, icone, cor, loading, badge, active, onClick }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={active ? 8 : 2}
      onClick={onClick}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: active ? 'translateY(-4px)' : 'none',
        border: active ? `1px solid ${cor}` : '1px solid transparent',
        boxShadow: active ? `0 8px 16px ${alpha(cor, 0.2)}` : theme.shadows[2],
        '&:hover': {
          boxShadow: active ? `0 8px 16px ${alpha(cor, 0.3)}` : theme.shadows[6],
          transform: 'translateY(-4px)',
          borderColor: alpha(cor, 0.5)
        },
      }}
    >
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${cor} 0%, ${alpha(cor, 0.45)} 100%)` }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            {titulo}
          </Typography>
          <Avatar sx={{ bgcolor: alpha(cor, 0.12), color: cor, width: 34, height: 34 }}>
            {React.cloneElement(icone, { sx: { fontSize: 18 } })}
          </Avatar>
        </Box>

        {loading ? (
          <Skeleton width="55%" height={36} />
        ) : (
          <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.1, mb: 0.4 }}>
            {valor}
          </Typography>
        )}

        {subtitulo && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {loading ? <Skeleton width="70%" /> : subtitulo}
          </Typography>
        )}
        {badge}
      </Box>
    </Paper>
  );
};

// ─── Cabeçalho de seção ───────────────────────────────────────────────────────

const SecaoHeader: React.FC<{ titulo: string; subtitulo?: string }> = ({ titulo, subtitulo }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle2" fontWeight={700}>
      {titulo}
    </Typography>
    {subtitulo && (
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        {subtitulo}
      </Typography>
    )}
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

// ─── Tooltip customizado ──────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={4} sx={{ p: 1.5, borderRadius: 1.5, minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontSize: '0.74rem' }}>
            {entry.name}: <strong>{fmtInt(entry.value)}</strong>
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────

interface Props {
  escritorioId?: string | null;
}

const ClientesDashboardTab: React.FC<Props> = ({ escritorioId }) => {
  const theme = useTheme();

  const cores = {
    ativo: theme.palette.success.main,
    bloqueado: theme.palette.warning.main,
    cancelado: theme.palette.error.main,
    aguardando: theme.palette.info.main,
    primario: theme.palette.primary.main,
    secundario: theme.palette.secondary.main,
  };

  // ── Estados p/ tabelas expandidas nos gráficos ─────────────────────────────
  const [selectedRegMonth, setSelectedRegMonth] = useState<string | null>(null);
  const [selectedOverdueMonth, setSelectedOverdueMonth] = useState<string | null>(null);
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);
  /** Bairro exibido no gráfico (inclui "Não informado" e "Demais bairros") */
  const [selectedBairroContrato, setSelectedBairroContrato] = useState<string | null>(null);
  const [selectedTipoCliente, setSelectedTipoCliente] = useState<'pf' | 'pj' | null>(null);
  const [selectedComoConheceuOrigem, setSelectedComoConheceuOrigem] = useState<string | null>(null);
  const [regTablePage, setRegTablePage] = useState(0);
  const [overdueTablePage, setOverdueTablePage] = useState(0);
  const [kpiTablePage, setKpiTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const tableRef = useRef<HTMLDivElement>(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: dashStats, isLoading: loadingDash } = useQuery({
    queryKey: ['dashboard-stats', escritorioId],
    queryFn: () => dashboardService.getStats(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [comoConheceuDateRange, setComoConheceuDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });

  const { data: clientStatusChart, isLoading: loadingClientStatus } = useQuery({
    queryKey: ['dashboard-client-status', escritorioId],
    queryFn: () => dashboardService.getClientStatusChart(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const comoConheceuInicio = comoConheceuDateRange.startDate ? comoConheceuDateRange.startDate.toISOString().split('T')[0] : undefined;
  const comoConheceuFim = comoConheceuDateRange.endDate ? comoConheceuDateRange.endDate.toISOString().split('T')[0] : undefined;

  const { data: comoConheceuChart, isLoading: loadingComoConheceu } = useQuery({
    queryKey: ['dashboard-como-conheceu', escritorioId, comoConheceuInicio, comoConheceuFim],
    queryFn: () => dashboardService.getComoConheceuChart(comoConheceuInicio, comoConheceuFim),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Clientes filtrados por origem (como conheceu)
  const { data: comoConheceuClientsResp, isLoading: loadingComoConheceuClients } = useQuery({
    queryKey: ['dashboard-como-conheceu-clients', selectedComoConheceuOrigem, comoConheceuInicio, comoConheceuFim, escritorioId],
    queryFn: async () => {
      if (!selectedComoConheceuOrigem) return { data: [] };
      return clientService.getAll({
        page: 1,
        limit: 1000,
        como_conheceu: selectedComoConheceuOrigem,
        data_inicio: comoConheceuInicio,
        data_fim: comoConheceuFim,
      } as any);
    },
    enabled: !!selectedComoConheceuOrigem,
    staleTime: 60_000,
  });

  const comoConheceuClients: Client[] = (comoConheceuClientsResp as any)?.data ?? [];

  // ── Alertas e Efeitos ──────────────────────────────────────────────────────

  // Efeito p/ scroll automático quando seleciona algo p/ ver na tabela
  useEffect(() => {
    if (selectedRegMonth || selectedOverdueMonth || selectedKpi || selectedBairroContrato || selectedTipoCliente || selectedComoConheceuOrigem) {
      // Pequeno timeout para garantir que o Collapse começou a renderizar
      setTimeout(() => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [selectedRegMonth, selectedOverdueMonth, selectedKpi, selectedBairroContrato, selectedTipoCliente, selectedComoConheceuOrigem]);

  // Alerta de clientes suspensos
  useEffect(() => {
    if (dashStats?.suspendedClients && dashStats.suspendedClients > 0) {
      Swal.fire({
        title: 'Atenção!',
        text: `Existem clientes com contrato suspenso. Resolva o quanto antes as pendências para normalizar o serviço.`,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  }, [dashStats?.suspendedClients, theme.palette.primary.main]);


  // Gráfico cadastros × cancelamentos (usa data_contrato e data_cancelamento)
  const { data: regChart, isLoading: loadingReg } = useQuery({
    queryKey: ['dashboard-registrations', escritorioId],
    queryFn: () => dashboardService.getRegistrationsCancellationsChart(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Gráfico de clientes com faturas vencidas por mês
  const { data: overdueClientsChart, isLoading: loadingOverdue } = useQuery({
    queryKey: ['dashboard-overdue-clients', escritorioId],
    queryFn: () => dashboardService.getOverdueClientsChart(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: contractStatsResp, isLoading: loadingContracts } = useQuery({
    queryKey: ['contract-stats', escritorioId],
    queryFn: () => contractService.getStats(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: clientsResp, isLoading: loadingClients } = useQuery({
    queryKey: ['clients-dashboard-count', escritorioId],
    queryFn: () => clientService.getAll({
      page: 1, limit: 1,
      ...(escritorioId ? { escritorio_id: escritorioId } as any : {}),
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // ── Dados derivados ────────────────────────────────────────────────────────

  const contractStats = (contractStatsResp as any)?.data ?? contractStatsResp;
  const totalContratos = contractStats?.total ?? 0;
  const contratosAtivos = contractStats?.ativos ?? 0;
  const contratosBloqueados = contractStats?.bloqueados ?? 0;
  const contratosCancelados = contractStats?.cancelados ?? 0;
  const contratosAguardando = contractStats?.aguardando_instalacao ?? 0;
  const contratosAguardandoAssinatura = contractStats?.aguardando_assinatura ?? 0;

  const totalClientes = (clientsResp as any)?.pagination?.total ?? dashStats?.totalClients ?? 0;
  const clientesAtivos = dashStats?.activeClients ?? 0;
  const clientesInativos = dashStats?.inactiveClients ?? 0;
  const inadimplentes = dashStats?.defaulters ?? 0;
  const faturasVencidas = dashStats?.overdueInvoices ?? 0;
  const suspensos = dashStats?.suspendedClients ?? 0;
  const chamadosAbertos = dashStats?.pendingTickets ?? 0;
  const crescimento = (dashStats as any)?.crescimentoMensal ?? 0;

  // Dados do gráfico de pizza – status clientes
  const clientePieData = useMemo(() => {
    if (!clientStatusChart || !Array.isArray(clientStatusChart)) return [];
    const corMap: Record<string, string> = {
      Ativo: cores.ativo,
      Inativo: '#9e9e9e',
      Suspenso: cores.bloqueado,
      Inadimplente: cores.cancelado,
    };
    return (clientStatusChart as any[])
      .filter((c) => (c.count ?? c.value ?? 0) > 0)
      .map((c) => ({
        name: c.status ?? c.name,
        value: Number(c.count ?? c.value ?? 0),
        color: corMap[c.status ?? c.name] ?? cores.primario,
      }));
  }, [clientStatusChart]);

  // Dados do gráfico de pizza – status contratos
  const contratoPieData = [
    { name: 'Ativos', value: contratosAtivos, color: cores.ativo },
    { name: 'Bloqueados', value: contratosBloqueados, color: cores.bloqueado },
    { name: 'Aguardando', value: contratosAguardando, color: cores.aguardando },
    { name: 'Ag. Assinatura', value: contratosAguardandoAssinatura, color: '#9e9e9e' },
    { name: 'Cancelados', value: contratosCancelados, color: cores.cancelado },
  ].filter((d) => d.value > 0);

  // Dados do gráfico de barras – cadastros vs cancelamentos de contratos
  const rawRegChart = useMemo(() => {
    if (!regChart || !Array.isArray(regChart)) return [];
    return (regChart as any[]).slice(-12);
  }, [regChart]);

  const regData = useMemo(() => {
    return rawRegChart.map((r: any) => ({
      mes: fmtMes(r.month ?? r.mes ?? ''),
      monthRaw: r.month ?? r.mes ?? '',
      cadastros: Number(r.registrations ?? r.cadastros ?? 0),
      cancelamentos: Number(r.cancellations ?? r.cancelamentos ?? 0),
    }));
  }, [rawRegChart]);

  // Dados do gráfico de clientes inadimplentes por mês
  const rawOverdueChart = useMemo(() => {
    if (!overdueClientsChart || !Array.isArray(overdueClientsChart)) return [];
    return (overdueClientsChart as any[]).slice(-12);
  }, [overdueClientsChart]);

  const overdueData = useMemo(() => {
    return rawOverdueChart.map((r: any) => ({
      mes: fmtMes(r.month ?? r.mes ?? ''),
      monthRaw: r.month ?? r.mes ?? '',
      clientes: Number(r.clientes ?? r.clients ?? 0),
    }));
  }, [rawOverdueChart]);

  // Contratos não cancelados por bairro de instalação (API sem `status` exclui cancelados)
  const { data: contractsForBairroRaw, isLoading: loadingContractsBairro } = useQuery({
    queryKey: ['dashboard-contracts-por-bairro', escritorioId],
    queryFn: async () => {
      const all: Contract[] = [];
      let page = 1;
      let pages = 1;
      const limit = 2000;
      const maxPages = 80;
      do {
        const resp = await contractService.getAll({ page, limit, filters: {} });
        const data = (resp as any)?.data ?? [];
        all.push(...data);
        pages = (resp as any)?.pagination?.pages ?? 1;
        page++;
      } while (page <= pages && page <= maxPages);
      return all;
    },
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  const contratosPorBairroChart = useMemo(() => {
    const list = (contractsForBairroRaw ?? []).filter((c) => c.status !== 'cancelado');
    const map = new Map<string, number>();
    for (const c of list) {
      const raw = (c.bairro_inst ?? '').trim();
      const label = raw.length ? raw : 'Não informado';
      map.set(label, (map.get(label) ?? 0) + 1);
    }
    const sorted = Array.from(map.entries())
      .map(([name, contratos]) => ({ name, contratos }))
      .sort((a, b) => b.contratos - a.contratos);
    const topN = 12;
    if (sorted.length <= topN) return sorted;
    const head = sorted.slice(0, topN);
    const rest = sorted.slice(topN).reduce((s, x) => s + x.contratos, 0);
    if (rest > 0) head.push({ name: 'Demais bairros', contratos: rest });
    return head;
  }, [contractsForBairroRaw]);

  // Clientes com status ≠ inativo — backend: filtro_status `todos`
  const { data: clientsNaoInativosRaw, isLoading: loadingClientesTipo } = useQuery({
    queryKey: ['dashboard-clientes-nao-inativos-tipo', escritorioId],
    queryFn: async () => {
      const all: Client[] = [];
      let page = 1;
      let pages = 1;
      const limit = 2000;
      const maxPages = 80;
      do {
        const resp = await clientService.getAll({
          page,
          limit,
          filtro_status: 'todos',
          ...(escritorioId ? { escritorio_id: escritorioId } : {}),
        } as any);
        const data = (resp as any)?.data ?? [];
        all.push(...data);
        pages = (resp as any)?.pagination?.pages ?? 1;
        page++;
      } while (page <= pages && page <= maxPages);
      return all;
    },
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  const clientesTipoPieData = useMemo(() => {
    const list = (clientsNaoInativosRaw ?? []).filter((c) => c.status !== 'inativo');
    let pf = 0;
    let pj = 0;
    for (const c of list) {
      if (c.tipo_pessoa === 'pj') pj += 1;
      else pf += 1;
    }
    return [
      { name: 'Pessoa física', value: pf, color: theme.palette.primary.main },
      { name: 'Pessoa jurídica', value: pj, color: theme.palette.secondary.main },
    ].filter((d) => d.value > 0);
  }, [clientsNaoInativosRaw, theme.palette.primary.main, theme.palette.secondary.main]);

  const labelBairroInst = useCallback((c: Contract) => {
    const raw = (c.bairro_inst ?? '').trim();
    return raw.length ? raw : 'Não informado';
  }, []);

  /** Contratos do bairro clicado (mesma regra do gráfico: não cancelados; "Demais bairros" = fora do top 12) */
  const contratosPorBairroSelecionado = useMemo(() => {
    if (!selectedBairroContrato || !contractsForBairroRaw) return [];
    const all = contractsForBairroRaw.filter((c) => c.status !== 'cancelado');

    if (selectedBairroContrato === 'Não informado') {
      return all.filter((c) => !(c.bairro_inst ?? '').trim());
    }

    if (selectedBairroContrato !== 'Demais bairros') {
      return all.filter((c) => labelBairroInst(c) === selectedBairroContrato);
    }

    const map = new Map<string, number>();
    for (const c of all) {
      const label = labelBairroInst(c);
      map.set(label, (map.get(label) ?? 0) + 1);
    }
    const sorted = Array.from(map.entries())
      .map(([name, n]) => ({ name, contratos: n }))
      .sort((a, b) => b.contratos - a.contratos);
    const topN = 12;
    const topNames = new Set(sorted.slice(0, Math.min(topN, sorted.length)).map((x) => x.name));
    return all.filter((c) => !topNames.has(labelBairroInst(c)));
  }, [selectedBairroContrato, contractsForBairroRaw, labelBairroInst]);

  const clientesPorTipoSelecionado = useMemo(() => {
    if (!selectedTipoCliente || !clientsNaoInativosRaw) return [];
    const list = clientsNaoInativosRaw.filter((c) => c.status !== 'inativo');
    if (selectedTipoCliente === 'pj') return list.filter((c) => c.tipo_pessoa === 'pj');
    return list.filter((c) => c.tipo_pessoa !== 'pj');
  }, [selectedTipoCliente, clientsNaoInativosRaw]);

  // ── Queries condicionais p/ clientes nos gráficos ─────────────────────────

  // Clientes contratados no mês selecionado do gráfico de cadastros
  const { data: regClientsResp, isLoading: loadingRegClients } = useQuery({
    queryKey: ['dashboard-reg-clients', selectedRegMonth],
    queryFn: async () => {
      if (!selectedRegMonth) return { data: [] };
      const [ano, mes] = selectedRegMonth.split('-');
      const dataInicio = `${ano}-${mes}-01`;
      const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
      const dataFim = `${ano}-${mes}-${String(ultimoDia).padStart(2, '0')}`;
      return clientService.getAll({
        page: 1,
        limit: 500,
        data_inicio: dataInicio,
        data_fim: dataFim,
      } as any);
    },
    enabled: !!selectedRegMonth,
    staleTime: 60_000,
  });

  const regClients: Client[] = (regClientsResp as any)?.data ?? [];

  // Clientes com faturas vencidas no mês selecionado do gráfico de inadimplentes
  const { data: overdueClientsResp, isLoading: loadingOverdueClients } = useQuery({
    queryKey: ['dashboard-overdue-month-clients', selectedOverdueMonth],
    queryFn: async () => {
      if (!selectedOverdueMonth) return { data: [] };
      // Buscar faturas vencidas do mês e depois extrair clientes únicos
      const [ano, mes] = selectedOverdueMonth.split('-');
      const dataInicio = `${ano}-${mes}-01`;
      const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
      const dataFim = `${ano}-${mes}-${String(ultimoDia).padStart(2, '0')}`;
      const faturas = await invoiceService.getInvoices({
        page: 1,
        limit: 5000,
        status: 'vencido',
        data_inicio: dataInicio,
        data_fim: dataFim,
      } as any);
      // Extrair clientes únicos
      const clientesMap = new Map<number, Client>();
      (faturas?.data ?? []).forEach((f: any) => {
        if (f.cliente && !clientesMap.has(f.cliente.id)) {
          clientesMap.set(f.cliente.id, f.cliente as Client);
        }
      });
      return { data: Array.from(clientesMap.values()) };
    },
    enabled: !!selectedOverdueMonth,
    staleTime: 60_000,
  });

  const overdueClients: Client[] = (overdueClientsResp as any)?.data ?? [];

  // Clientes para KPIs selecionados
  const { data: kpiClientsResp, isLoading: loadingKpiClients } = useQuery({
    queryKey: ['dashboard-kpi-clients', selectedKpi, escritorioId],
    queryFn: async () => {
      if (!selectedKpi) return { data: [] };

      if (selectedKpi === 'inadimplentes') {
        const faturas = await invoiceService.getInvoices({
          status: 'vencido',
          limit: 2000,
        } as any);
        const clientesMap = new Map<number, Client>();
        (faturas?.data ?? []).forEach((f: any) => {
          if (f.cliente && !clientesMap.has(f.cliente.id)) {
            clientesMap.set(f.cliente.id, f.cliente as Client);
          }
        });
        return { data: Array.from(clientesMap.values()) };
      }

      if (selectedKpi === 'bloqueados') {
        return clientService.getAll({ status: 'bloqueado', limit: 1000 });
      }

      if (selectedKpi === 'ativos') {
        return clientService.getAll({ status: 'ativo', limit: 1000 });
      }

      if (selectedKpi === 'aguardando_instalacao') {
        return clientService.getAll({ status: 'aguardando_instalacao', limit: 1000 });
      }

      if (selectedKpi === 'cancelados') {
        return clientService.getAll({ status: 'cancelado', limit: 1000 });
      }

      if (selectedKpi === 'suspensos') {
        return clientService.getAll({ filtro_status: 'suspensos', limit: 1000 } as any);
      }

      if (selectedKpi === 'aguardando_assinatura') {
        return clientService.getAll({ status: 'aguardando_assinatura', limit: 1000 });
      }

      if (selectedKpi === 'chamados') {
        const resp = await ticketService.getAll({ filters: { status: 'aberto' }, limit: 1000 });
        const resp2 = await ticketService.getAll({ filters: { status: 'em_andamento' }, limit: 1000 });

        const all = [...(resp.data || []), ...(resp2.data || [])];
        const clientesMap = new Map<number, Client>();
        all.forEach((t: any) => {
          if (t.cliente && !clientesMap.has(t.cliente.id)) {
            clientesMap.set(t.cliente.id, t.cliente as Client);
          }
        });
        return { data: Array.from(clientesMap.values()) };
      }

      return { data: [] };
    },
    enabled: !!selectedKpi,
    staleTime: 60_000,
  });

  const kpiClients: Client[] = (kpiClientsResp as any)?.data ?? [];

  // Contratos para KPIs de contrato
  const { data: kpiContractsResp, isLoading: loadingKpiContracts } = useQuery({
    queryKey: ['dashboard-kpi-contracts', selectedKpi, escritorioId],
    queryFn: async () => {
      const contractStatuses = ['ativos', 'bloqueados', 'aguardando_instalacao', 'aguardando_assinatura', 'cancelados'];
      if (!selectedKpi || !contractStatuses.includes(selectedKpi)) return { data: [] };

      // O backend usa status no singular, mas as chaves das KPIs podem estar no plural ou com labels diferentes
      let statusFiltro = selectedKpi;
      if (statusFiltro === 'ativos') statusFiltro = 'ativo';
      if (statusFiltro === 'cancelados') statusFiltro = 'cancelado';
      if (statusFiltro === 'bloqueados') statusFiltro = 'bloqueado';

      return contractService.getAll({
        limit: 1000,
        filters: { status: statusFiltro }
      });
    },
    enabled: !!selectedKpi && ['ativos', 'bloqueados', 'aguardando_instalacao', 'aguardando_assinatura', 'cancelados'].includes(selectedKpi),
    staleTime: 60_000,
  });

  const kpiContracts: Contract[] = (kpiContractsResp as any)?.data ?? [];

  const isContractKpi = useMemo(() => {
    return selectedKpi ? ['ativos', 'bloqueados', 'aguardando_instalacao', 'aguardando_assinatura', 'cancelados'].includes(selectedKpi) : false;
  }, [selectedKpi]);

  // ── Handlers de clique ──────────────────────────────────────────────────────

  const handleKpiClick = useCallback((kpi: string) => {
    setSelectedRegMonth(null);
    setSelectedOverdueMonth(null);
    setSelectedBairroContrato(null);
    setSelectedTipoCliente(null);
    setSelectedKpi((prev) => (prev === kpi ? null : kpi));
    setKpiTablePage(0);
  }, []);

  const handleRegChartClick = useCallback((data: any) => {
    if (!data?.activePayload?.[0]?.payload?.monthRaw) return;
    const month = data.activePayload[0].payload.monthRaw;
    setSelectedOverdueMonth(null); // Fecha a outra se aberta
    setSelectedKpi(null);
    setSelectedBairroContrato(null);
    setSelectedTipoCliente(null);
    setSelectedRegMonth((prev) => prev === month ? null : month);
    setRegTablePage(0);
  }, []);

  const handleOverdueChartClick = useCallback((data: any) => {
    if (!data?.activePayload?.[0]?.payload?.monthRaw) return;
    const month = data.activePayload[0].payload.monthRaw;
    setSelectedRegMonth(null); // Fecha a outra se aberta
    setSelectedKpi(null);
    setSelectedBairroContrato(null);
    setSelectedTipoCliente(null);
    setSelectedOverdueMonth((prev) => prev === month ? null : month);
    setOverdueTablePage(0);
  }, []);

  const handleBairroChartClick = useCallback((state: any) => {
    const payload = state?.activePayload?.[0]?.payload;
    if (!payload?.name) return;
    setSelectedKpi(null);
    setSelectedRegMonth(null);
    setSelectedOverdueMonth(null);
    setSelectedTipoCliente(null);
    setSelectedBairroContrato((prev) => (prev === payload.name ? null : payload.name));
    setKpiTablePage(0);
  }, []);

  const handleTipoClientePieClick = useCallback((entry: any, index?: number) => {
    const name = (entry?.name ?? (typeof index === 'number' ? clientesTipoPieData[index]?.name : undefined)) as
      | string
      | undefined;
    let tipo: 'pf' | 'pj' | null = null;
    if (name === 'Pessoa física') tipo = 'pf';
    else if (name === 'Pessoa jurídica') tipo = 'pj';
    if (!tipo) return;
    setSelectedKpi(null);
    setSelectedRegMonth(null);
    setSelectedOverdueMonth(null);
    setSelectedBairroContrato(null);
    setSelectedComoConheceuOrigem(null);
    setSelectedTipoCliente((prev) => (prev === tipo ? null : tipo));
    setKpiTablePage(0);
  }, [clientesTipoPieData]);

  const handleComoConheceuChartClick = useCallback((state: any) => {
    const payload = state?.activePayload?.[0]?.payload;
    if (!payload?.origem) return;
    setSelectedKpi(null);
    setSelectedRegMonth(null);
    setSelectedOverdueMonth(null);
    setSelectedBairroContrato(null);
    setSelectedTipoCliente(null);
    setSelectedComoConheceuOrigem((prev) => (prev === payload.origem ? null : payload.origem));
    setKpiTablePage(0);
  }, []);

  // Saldo mensal (cadastros − cancelamentos) para indicador de tendência
  const ultimoMes = regData[regData.length - 1];
  const saldoUltimoMes = ultimoMes ? ultimoMes.cadastros - ultimoMes.cancelamentos : 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: 6 }}>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Total Clientes"
            valor={fmtInt(totalClientes)}
            subtitulo={`${fmtInt(clientesAtivos)} ativos`}
            icone={<PeopleAlt />}
            cor={cores.primario}
            loading={loadingClients}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Contratos Ativos"
            valor={fmtInt(contratosAtivos)}
            subtitulo={`de ${fmtInt(totalContratos)} totais`}
            icone={<Assignment />}
            cor={cores.ativo}
            loading={loadingContracts}
            active={selectedKpi === 'ativos'}
            onClick={() => handleKpiClick('ativos')}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Bloqueados"
            valor={fmtInt(contratosBloqueados)}
            subtitulo="contratos bloqueados"
            icone={<Block />}
            cor={cores.bloqueado}
            loading={loadingContracts}
            active={selectedKpi === 'bloqueados'}
            onClick={() => handleKpiClick('bloqueados')}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Inadimplentes"
            valor={fmtInt(inadimplentes)}
            subtitulo="clientes inadimplentes"
            icone={<Warning />}
            cor={'#f57c00'}
            loading={loadingDash}
            active={selectedKpi === 'inadimplentes'}
            onClick={() => handleKpiClick('inadimplentes')}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Suspenso"
            valor={fmtInt(suspensos)}
            subtitulo="clientes suspensos"
            icone={<Block />}
            cor={theme.palette.error.main}
            loading={loadingDash}
            active={selectedKpi === 'suspensos'}
            onClick={() => handleKpiClick('suspensos')}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            titulo="Chamados Abertos"
            valor={fmtInt(chamadosAbertos)}
            subtitulo="tickets pendentes"
            icone={<HourglassEmpty />}
            cor={cores.aguardando}
            loading={loadingDash}
            active={selectedKpi === 'chamados'}
            onClick={() => handleKpiClick('chamados')}
          />
        </Grid>

      </Grid>

      {/* ── Linha 1: gráfico grande + pizza contratos ─────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>

        {/* Cadastros × Cancelamentos (via data_contrato / data_cancelamento) */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Contratos: Cadastros × Cancelamentos
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Baseado em <strong>data_contrato</strong> e <strong>data_cancelamento</strong> — últimos 12 meses
                </Typography>
              </Box>
              {/* Saldo do último mês */}
              {ultimoMes && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.2,
                    py: 0.4,
                    borderRadius: 2,
                    bgcolor: alpha(
                      saldoUltimoMes > 0 ? cores.ativo : saldoUltimoMes < 0 ? cores.cancelado : theme.palette.text.disabled,
                      0.1
                    ),
                  }}
                >
                  {saldoUltimoMes > 0 ? (
                    <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : saldoUltimoMes < 0 ? (
                    <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                  ) : (
                    <TrendingFlat sx={{ fontSize: 16, color: 'text.disabled' }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      fontSize: '0.74rem',
                      color: saldoUltimoMes > 0 ? 'success.main' : saldoUltimoMes < 0 ? 'error.main' : 'text.disabled',
                    }}
                  >
                    {saldoUltimoMes > 0 ? '+' : ''}{fmtInt(saldoUltimoMes)} em {ultimoMes.mes}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {loadingReg ? (
              <Skeleton variant="rectangular" height={228} sx={{ borderRadius: 1 }} />
            ) : regData.length === 0 ? (
              <Box sx={{ display: 'flex', height: 228, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados de movimentação</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={228}>
                <BarChart data={regData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }} barGap={4} onClick={handleRegChartClick} style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.7)} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip content={<ChartTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="cadastros"
                    name="Cadastros"
                    fill={alpha(cores.ativo, 0.85)}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={32}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="cancelamentos"
                    name="Cancelamentos"
                    fill={alpha(cores.cancelado, 0.8)}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={32}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Donut – Status dos Contratos */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 320 }}>
            <SecaoHeader titulo="Status dos Contratos" subtitulo="Distribuição atual" />
            {loadingContracts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 245 }}>
                <Skeleton variant="circular" width={170} height={170} />
              </Box>
            ) : contratoPieData.length === 0 ? (
              <Box sx={{ display: 'flex', height: 245, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={245}>
                <PieChart>
                  <Pie
                    data={contratoPieData}
                    cx="50%"
                    cy="44%"
                    innerRadius={62}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {contratoPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v: any, n: any) => [fmtInt(v), n]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Contratos por bairro + Clientes por tipo (PF/PJ) ───────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, minHeight: 360 }}>
            <SecaoHeader
              titulo="Contratos por bairro"
              subtitulo="Apenas contratos não cancelados — bairro de instalação (top 12 + demais). Clique na barra para listar."
            />
            {loadingContractsBairro ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
            ) : contratosPorBairroChart.length === 0 ? (
              <Box sx={{ display: 'flex', height: 280, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados de bairro</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  layout="vertical"
                  data={contratosPorBairroChart}
                  margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                  barCategoryGap={12}
                  onClick={handleBairroChartClick}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.7)} horizontal />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={118}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: string) => (v.length > 22 ? `${v.slice(0, 22)}…` : v)}
                  />
                  <ReTooltip
                    formatter={(v: number) => [fmtInt(v), 'Contratos']}
                    labelFormatter={(l: string) => l}
                  />
                  <Bar
                    dataKey="contratos"
                    name="Contratos"
                    fill={alpha(cores.primario, 0.88)}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={22}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, minHeight: 360 }}>
            <SecaoHeader
              titulo="Clientes por tipo"
              subtitulo="Pessoa física e jurídica — exclui clientes inativos. Clique na fatia para listar."
            />
            {loadingClientesTipo ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                <Skeleton variant="circular" width={200} height={200} />
              </Box>
            ) : clientesTipoPieData.length === 0 ? (
              <Box sx={{ display: 'flex', height: 280, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart style={{ cursor: 'pointer' }}>
                  <Pie
                    data={clientesTipoPieData}
                    cx="50%"
                    cy="48%"
                    innerRadius={68}
                    outerRadius={108}
                    paddingAngle={4}
                    dataKey="value"
                    onClick={handleTipoClientePieClick}
                  >
                    {clientesTipoPieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" cursor="pointer" />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v: any, n: any) => [fmtInt(v), n]} />
                  <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Gráfico Inadimplentes por Mês ─────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Clientes com Faturas Vencidas
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Quantidade de clientes distintos com faturas vencidas por mês — últimos 12 meses
                </Typography>
              </Box>
              {overdueData.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.2,
                    py: 0.4,
                    borderRadius: 2,
                    bgcolor: alpha(cores.cancelado, 0.08),
                  }}
                >
                  <Warning sx={{ fontSize: 15, color: cores.cancelado }} />
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.74rem', color: cores.cancelado }}>
                    {overdueData[overdueData.length - 1]?.clientes ?? 0} no mês atual
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            {loadingOverdue ? (
              <Skeleton variant="rectangular" height={228} sx={{ borderRadius: 1 }} />
            ) : overdueData.length === 0 ? (
              <Box sx={{ display: 'flex', height: 228, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados de inadimplência</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={228}>
                <AreaChart data={overdueData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }} onClick={handleOverdueChartClick} style={{ cursor: 'pointer' }}>
                  <defs>
                    <linearGradient id="gradienteInadimplente" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={cores.cancelado} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={cores.cancelado} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.7)} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="clientes"
                    name="Clientes inadimplentes"
                    stroke={cores.cancelado}
                    strokeWidth={2.5}
                    fill="url(#gradienteInadimplente)"
                    dot={{ r: 4, fill: cores.cancelado, stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
                    activeDot={{ r: 6, fill: cores.cancelado, stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Gráfico / Quadro Origem dos Clientes (Como conheceu a Empresa?) ────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Como conheceu a Empresa?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Quantidade total de clientes cadastrados por cada meio de origem
                </Typography>
              </Box>
              <Box sx={{ minWidth: 260 }}>
                <SysDateRangePicker
                  label="Data de Cadastro"
                  value={comoConheceuDateRange}
                  onChange={(range) => setComoConheceuDateRange(range)}
                />
              </Box>
            </Box>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            {loadingComoConheceu ? (
              <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1 }} />
            ) : !comoConheceuChart || comoConheceuChart.length === 0 ? (
              <Box sx={{ display: 'flex', height: 200, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados de origem dos clientes</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(260, comoConheceuChart.length * 32)}>
                <BarChart
                  layout="vertical"
                  data={comoConheceuChart}
                  margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                  barCategoryGap={8}
                  onClick={handleComoConheceuChartClick}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.7)} horizontal />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="origem"
                    width={160}
                    tick={{ fontSize: 11, fontWeight: 500 }}
                  />
                  <ReTooltip
                    formatter={(v: number) => [fmtInt(v), 'Clientes']}
                    labelFormatter={(l: string) => `Origem: ${l}`}
                  />
                  <Bar
                    dataKey="quantidade"
                    name="Clientes"
                    fill={alpha(cores.primario, 0.85)}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={24}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>


      {/* ── Linha 2: quadro contratos + pizza clientes + resumo clientes ───── */}
      <Grid container spacing={2}>

        {/* Barras de progresso – contratos por status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 300 }}>
            <SecaoHeader titulo="Resumo de Contratos por Status" />
            {loadingContracts ? (
              <Stack spacing={1.5}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                ))}
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                {[
                  { label: 'Ativos', value: contratosAtivos, color: cores.ativo, icon: <CheckCircle sx={{ fontSize: 15 }} />, onClick: () => handleKpiClick('ativos') },
                  { label: 'Bloqueados', value: contratosBloqueados, color: cores.bloqueado, icon: <Block sx={{ fontSize: 15 }} />, onClick: () => handleKpiClick('bloqueados') },
                  { label: 'Ag. Instalação', value: contratosAguardando, color: cores.aguardando, icon: <HourglassEmpty sx={{ fontSize: 15 }} />, onClick: () => handleKpiClick('aguardando_instalacao') },
                  { label: 'Ag. Assinatura', value: contratosAguardandoAssinatura, color: '#9e9e9e', icon: <History sx={{ fontSize: 15 }} />, onClick: () => handleKpiClick('aguardando_assinatura') },
                  { label: 'Cancelados', value: contratosCancelados, color: cores.cancelado, icon: <Cancel sx={{ fontSize: 15 }} />, onClick: () => handleKpiClick('cancelados') },
                ].map((item) => {
                  const pct = totalContratos > 0 ? (item.value / totalContratos) * 100 : 0;
                  const isActive = selectedKpi === (
                    item.label === 'Ativos' ? 'ativos' :
                      item.label === 'Bloqueados' ? 'bloqueados' :
                        item.label === 'Ag. Instalação' ? 'aguardando_instalacao' :
                          item.label === 'Ag. Assinatura' ? 'aguardando_assinatura' :
                            item.label === 'Cancelados' ? 'cancelados' : null
                  );

                  return (
                    <Box
                      key={item.label}
                      onClick={item.onClick}
                      sx={{
                        cursor: 'pointer',
                        p: 0,
                        borderRadius: 1,
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: alpha(item.color, 0.05) },
                        ...(isActive && { bgcolor: alpha(item.color, 0.1) })
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: item.color }}>
                          {item.icon}
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.74rem', color: 'text.primary' }}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                          <Typography variant="caption" fontWeight={700} sx={{ color: item.color, fontSize: '0.76rem' }}>
                            {fmtInt(item.value)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.66rem' }}>
                            ({pct.toFixed(1)}%)
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(item.color, 0.1),
                          '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  );
                })}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Total</Typography>
                  <Typography variant="subtitle2" fontWeight={800} color="primary.main">{fmtInt(totalContratos)}</Typography>
                </Box>
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Donut – Status dos Clientes */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 300 }}>
            <SecaoHeader titulo="Status dos Clientes" subtitulo="Distribuição atual" />
            {loadingClientStatus ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 205 }}>
                <Skeleton variant="circular" width={140} height={140} />
              </Box>
            ) : clientePieData.length === 0 ? (
              <Box sx={{ display: 'flex', height: 205, alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.disabled">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={205}>
                <PieChart>
                  <Pie
                    data={clientePieData}
                    cx="50%"
                    cy="43%"
                    innerRadius={48}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {clientePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(v: any, n: any) => [fmtInt(v), n]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Resumo de Clientes */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: 300 }}>
            <SecaoHeader titulo="Clientes" subtitulo="Resumo geral" />
            <Stack spacing={1.3} sx={{ mt: 0.5 }}>
              {[
                { label: 'Total cadastrados', value: totalClientes, cor: cores.primario },
                { label: 'Ativos', value: clientesAtivos, cor: cores.ativo },
                { label: 'Desativados', value: clientesInativos, cor: '#9e9e9e' },
                { label: 'Inadimplentes', value: inadimplentes, cor: '#f57c00' },
              ].map((item) => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.cor, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant="caption" fontWeight={700} sx={{ color: item.cor, fontSize: '0.83rem' }}>
                    {loadingClients || loadingDash ? <Skeleton width={38} /> : fmtInt(item.value)}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {/* Taxa de ativação */}
            {!loadingClients && !loadingDash && totalClientes > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.71rem' }}>
                    Taxa de clientes ativos
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: '0.73rem' }}>
                    {((clientesAtivos / totalClientes) * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(clientesAtivos / totalClientes) * 100}
                  sx={{
                    height: 7,
                    borderRadius: 4,
                    bgcolor: alpha(cores.ativo, 0.1),
                    '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 4 },
                  }}
                />
              </>
            )}
          </Paper>
        </Grid>

      </Grid>

      {/* ── Tabela Unificada de Detalhes (Dashboard) ─────────────────────── */}
      <Box ref={tableRef}>
        {isContractKpi || selectedBairroContrato ? (
          <ContratosTable
            contratos={selectedBairroContrato ? contratosPorBairroSelecionado : kpiContracts}
            loading={selectedBairroContrato ? loadingContractsBairro : loadingKpiContracts}
            dismissible
            open={!!selectedKpi || !!selectedBairroContrato}
            onClose={() => {
              setSelectedKpi(null);
              setSelectedBairroContrato(null);
            }}
            titulo={
              selectedBairroContrato
                ? `Contratos — ${selectedBairroContrato}`
                : selectedKpi === 'ativos' ? 'Contratos Ativos' :
                  selectedKpi === 'bloqueados' ? 'Contratos Bloqueados' :
                    selectedKpi === 'aguardando_instalacao' ? 'Contratos Aguardando Instalação' :
                      selectedKpi === 'aguardando_assinatura' ? 'Contratos Aguardando Assinatura' :
                        'Contratos Cancelados'
            }
            subtitulo={`${selectedBairroContrato ? contratosPorBairroSelecionado.length : kpiContracts.length} contrato(s) encontrado(s)`}
            headerColor={
              selectedBairroContrato
                ? cores.primario
                : selectedKpi === 'ativos' ? cores.ativo :
                  selectedKpi === 'bloqueados' ? cores.bloqueado :
                    selectedKpi === 'aguardando_instalacao' ? cores.aguardando :
                      selectedKpi === 'aguardando_assinatura' ? '#9e9e9e' :
                        cores.cancelado
            }
            page={kpiTablePage}
            rowsPerPage={rowsPerPage}
            onPageChange={setKpiTablePage}
            onRowsPerPageChange={(n) => { setRowsPerPage(n); setKpiTablePage(0); }}
          />
        ) : (
          <ClientesTable
            clientes={
              selectedComoConheceuOrigem ? comoConheceuClients :
                selectedTipoCliente ? clientesPorTipoSelecionado :
                  selectedKpi ? kpiClients :
                    selectedRegMonth ? regClients :
                      overdueClients
            }
            loading={
              selectedComoConheceuOrigem ? loadingComoConheceuClients :
                selectedTipoCliente ? loadingClientesTipo :
                  selectedKpi ? loadingKpiClients :
                    selectedRegMonth ? loadingRegClients :
                      loadingOverdueClients
            }
            dismissible
            open={!!selectedRegMonth || !!selectedOverdueMonth || !!selectedKpi || !!selectedTipoCliente || !!selectedComoConheceuOrigem}
            onClose={() => {
              setSelectedRegMonth(null);
              setSelectedOverdueMonth(null);
              setSelectedKpi(null);
              setSelectedTipoCliente(null);
              setSelectedComoConheceuOrigem(null);
            }}
            titulo={
              selectedComoConheceuOrigem ? `Clientes — Conheceu por: ${selectedComoConheceuOrigem}` :
                selectedTipoCliente === 'pf' ? 'Clientes — Pessoa física' :
                  selectedTipoCliente === 'pj' ? 'Clientes — Pessoa jurídica' :
                    selectedKpi === 'inadimplentes' ? 'Clientes Inadimplentes' :
                      selectedKpi === 'bloqueados' ? 'Clientes Bloqueados' :
                        selectedKpi === 'suspensos' ? 'Clientes Suspensos' :
                          selectedKpi === 'chamados' ? 'Clientes com Chamados Abertos' :
                            selectedKpi === 'aguardando_assinatura' ? 'Aguardando Assinatura' :
                              selectedRegMonth ? `Clientes contratados em ${fmtMes(selectedRegMonth)}` :
                                `Clientes com faturas vencidas em ${selectedOverdueMonth ? fmtMes(selectedOverdueMonth) : ''}`
            }
            subtitulo={
              selectedComoConheceuOrigem
                ? `${comoConheceuClients.length} cliente(s) encontrado(s)`
                : selectedTipoCliente
                  ? `${clientesPorTipoSelecionado.length} cliente(s) encontrado(s)`
                  : selectedKpi ? `${kpiClients.length} cliente(s) encontrado(s)` :
                    selectedRegMonth ? `${regClients.length} cliente(s) encontrado(s)` :
                      `${overdueClients.length} cliente(s) encontrado(s)`
            }
            headerColor={
              selectedComoConheceuOrigem ? cores.primario :
                selectedTipoCliente === 'pf' ? theme.palette.primary.main :
                  selectedTipoCliente === 'pj' ? theme.palette.secondary.main :
                    selectedKpi === 'inadimplentes' ? '#f57c00' :
                      selectedKpi === 'bloqueados' ? cores.bloqueado :
                        selectedKpi === 'suspensos' ? cores.bloqueado :
                          selectedKpi === 'chamados' ? cores.aguardando :
                            selectedKpi === 'aguardando_assinatura' ? '#9e9e9e' :
                              selectedRegMonth ? cores.ativo :
                                cores.cancelado
            }
            page={
              selectedTipoCliente || selectedKpi ? kpiTablePage :
                selectedRegMonth ? regTablePage :
                  overdueTablePage
            }
            rowsPerPage={rowsPerPage}
            onPageChange={
              selectedTipoCliente || selectedKpi ? setKpiTablePage :
                selectedRegMonth ? setRegTablePage :
                  setOverdueTablePage
            }
            onRowsPerPageChange={(n) => {
              setRowsPerPage(n);
              if (selectedTipoCliente || selectedKpi) setKpiTablePage(0);
              else if (selectedRegMonth) setRegTablePage(0);
              else setOverdueTablePage(0);
            }}
          />
        )}
      </Box>
    </Box>

  );
};

export default ClientesDashboardTab;
